import { GridColDef } from '@mui/x-data-grid';
import { Mapper } from './Mapper';
import { FilterDefinition } from '../models/FilterDefinition/FilterDefinition';

export interface GridColDefMapperOptions {
  /** include columns with zero or negative columnOrder */
  includeZeroOrderFields?: boolean; // default: false
  /** extra predicate to further filter down the set */
  customFilter?: (def: FilterDefinition) => boolean;
}

export class FilterDefinitionGridColDefMapper implements Mapper<
  FilterDefinition[],
  GridColDef[]
> {
  map(
    filterDefinitions: FilterDefinition[],
    options: GridColDefMapperOptions = {}
  ): GridColDef[] {
    const { includeZeroOrderFields = false, customFilter } = options;

    const seenColumnNames = new Set<string>();

    // must be database fields
    const dbFields = filterDefinitions.filter(fd => fd.isDatabaseField);

    // split into exported (columnOrder>0) and zero-order
    const exported = dbFields.filter(fd => fd.csvMetadata.columnOrder > 0);
    const zeroOrder = dbFields.filter(fd => fd.csvMetadata.columnOrder <= 0);

    // apply custom filter if provided
    const applyFilter = (fd: FilterDefinition) =>
      !customFilter || customFilter(fd);

    const sortedExported = exported
      .filter(applyFilter)
      .sort((a, b) => a.csvMetadata.columnOrder - b.csvMetadata.columnOrder);

    // optional include zeroOrder
    const finalFields = includeZeroOrderFields
      ? [...zeroOrder.filter(applyFilter), ...sortedExported]
      : sortedExported;

    // remove duplicates by csvMetadata.columnName
    const uniqueFields = finalFields.filter(fd => {
      const name = fd.csvMetadata.columnName;
      if (!seenColumnNames.has(name)) {
        seenColumnNames.add(name);
        return true;
      }
      return false;
    });

    // map to GridColDef and remove field duplicates
    const result: GridColDef[] = [];
    const seenFields = new Set<string>();
    uniqueFields.forEach(fd => {
      const field = fd.databaseMetadata?.columnName ?? '';
      if (!seenFields.has(field)) {
        seenFields.add(field);
        result.push({
          field,
          headerName: fd.csvMetadata.columnName,
        });
      }
    });
    return result;
  }
}
